import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Database, KeyRound, ShieldCheck, AlertTriangle } from "lucide-react";

type ErrorEntry = { at: string; where: string; message: string };
type PolicyRow = { tablename: string; policyname: string; cmd: string };

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string;
const BACKEND_REGION = "ap-southeast-2 (Sydney)";
const BACKEND_INSTANCE_SIZE = "Tiny";
// Fallback list of buckets this app reads/writes, used if listBuckets() is restricted.
const KNOWN_BUCKETS = ["vision-board", "parent-guide"];

// Lightweight runtime error capture so the admin screen can show what failed.
const ERR_KEY = "wekit_admin_recent_errors";
function pushError(where: string, message: string) {
  try {
    const list: ErrorEntry[] = JSON.parse(localStorage.getItem(ERR_KEY) ?? "[]");
    list.unshift({ at: new Date().toISOString(), where, message: String(message).slice(0, 400) });
    localStorage.setItem(ERR_KEY, JSON.stringify(list.slice(0, 25)));
  } catch { /* ignore */ }
}
if (typeof window !== "undefined" && !(window as unknown as { __wekitErrHooked?: boolean }).__wekitErrHooked) {
  (window as unknown as { __wekitErrHooked?: boolean }).__wekitErrHooked = true;
  window.addEventListener("error", (e) => pushError("window.error", e.message));
  window.addEventListener("unhandledrejection", (e) => pushError("promise", String(e.reason?.message ?? e.reason)));
}

const AdminBackend = () => {
  const { user, session } = useAuth();
  const [sessionInfo, setSessionInfo] = useState<{ uid: string; email: string; roles: string[] } | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [pingState, setPingState] = useState<"idle" | "running" | "ok" | "fail">("idle");
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [policies, setPolicies] = useState<PolicyRow[]>([]);
  const [errors, setErrors] = useState<ErrorEntry[]>([]);
  const [buckets, setBuckets] = useState<string[] | null>(null);
  const [bucketsError, setBucketsError] = useState<string | null>(null);

  useEffect(() => {
    try { setErrors(JSON.parse(localStorage.getItem(ERR_KEY) ?? "[]")); } catch { /* noop */ }
  }, []);

  const loadBuckets = async () => {
    const { data, error } = await supabase.storage.listBuckets();
    if (error) {
      setBucketsError(error.message);
      setBuckets(KNOWN_BUCKETS);
      return;
    }
    setBucketsError(null);
    setBuckets(data.length ? data.map((b) => b.name) : KNOWN_BUCKETS);
  };


  const runPing = async () => {
    setPingState("running");
    const t0 = performance.now();
    const { error } = await supabase.from("profiles").select("id", { count: "exact", head: true });
    const dt = Math.round(performance.now() - t0);
    setPingMs(dt);
    if (error) {
      pushError("ping:profiles", error.message);
      setErrors(JSON.parse(localStorage.getItem(ERR_KEY) ?? "[]"));
      setPingState("fail");
    } else {
      setPingState("ok");
    }
  };

  const testSession = async () => {
    setSessionLoading(true);
    setSessionInfo(null);
    const { data: u, error: uErr } = await supabase.auth.getUser();
    if (uErr || !u.user) {
      pushError("auth.getUser", uErr?.message ?? "no_user");
      setErrors(JSON.parse(localStorage.getItem(ERR_KEY) ?? "[]"));
      setSessionLoading(false);
      return;
    }
    const { data: roles, error: rErr } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", u.user.id);
    if (rErr) pushError("user_roles.select", rErr.message);
    setSessionInfo({
      uid: u.user.id,
      email: u.user.email ?? "—",
      roles: (roles ?? []).map((r) => r.role as string),
    });
    setSessionLoading(false);
  };

  const loadPolicies = async () => {
    // pg_policies isn't exposed via PostgREST; show a curated summary instead.
    // We hard-code the snapshot generated at build time from /docs/supabase-overview.md.
    setPolicies(POLICY_SNAPSHOT);
  };

  useEffect(() => {
    runPing();
    loadPolicies();
    loadBuckets();
  }, []);


  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Admin Hub
          </Link>
          <h1 className="font-display text-lg font-bold">Backend Console</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
        {/* Connectivity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> Connectivity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Row label="Instance ID" value={PROJECT_ID || "—"} mono />
              <Row label="Region" value={BACKEND_REGION} />
              <Row label="Instance size" value={BACKEND_INSTANCE_SIZE} />
              <Row label="Project URL" value={SUPABASE_URL || "—"} mono />
              <Row label="Client module" value="src/integrations/supabase/client.ts" mono />
              <Row label="Auth context" value="src/contexts/AuthContext.tsx" mono />
            </div>
            <div className="pt-2">
              <div className="text-sm text-muted-foreground mb-1.5">Storage buckets</div>
              {buckets === null ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {buckets.map((b) => (
                    <Badge key={b} variant="outline" className="font-mono text-xs">{b}</Badge>
                  ))}
                </div>
              )}
              {bucketsError && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Live listing unavailable ({bucketsError}) — showing buckets referenced by the app.
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button onClick={runPing} disabled={pingState === "running"} size="sm">
                {pingState === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Re-test"}
              </Button>

              {pingState === "ok" && (
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" /> Reachable {pingMs !== null && `(${pingMs} ms)`}
                </span>
              )}
              {pingState === "fail" && (
                <span className="inline-flex items-center gap-1.5 text-sm text-destructive">
                  <XCircle className="h-4 w-4" /> Failed — see recent errors
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Auth session */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Auth Session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {user ? `Signed in as ${user.email}` : "No active session"}
            </div>
            <Button onClick={testSession} disabled={sessionLoading} size="sm">
              {sessionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Test current session
            </Button>
            {sessionInfo && (
              <div className="rounded-lg border border-border/50 bg-muted/30 p-4 text-sm space-y-1.5">
                <Row label="User ID" value={sessionInfo.uid} mono />
                <Row label="Email" value={sessionInfo.email} />
                <Row
                  label="Roles"
                  value={
                    sessionInfo.roles.length ? sessionInfo.roles.join(", ") : "(none — default user)"
                  }
                />
                <Row label="Access token expires" value={session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : "—"} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* RLS summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Row Level Security</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Snapshot of current policies on tables used by the app. All public tables have RLS enabled.
            </p>
            <div className="max-h-96 overflow-auto rounded-lg border border-border/50">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Table</th>
                    <th className="px-3 py-2 font-medium">Policy</th>
                    <th className="px-3 py-2 font-medium">Cmd</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p, i) => (
                    <tr key={i} className="border-t border-border/40">
                      <td className="px-3 py-1.5 font-mono text-xs">{p.tablename}</td>
                      <td className="px-3 py-1.5">{p.policyname}</td>
                      <td className="px-3 py-1.5">
                        <Badge variant="outline" className="text-xs">{p.cmd}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent errors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Recent Errors</CardTitle>
          </CardHeader>
          <CardContent>
            {errors.length === 0 ? (
              <p className="text-sm text-muted-foreground">No errors captured this session.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {errors.map((e, i) => (
                  <li key={i} className="rounded-md border border-border/40 bg-muted/20 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs">{e.where}</span>
                      <span className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 break-words">{e.message}</div>
                  </li>
                ))}
              </ul>
            )}
            {errors.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => { localStorage.removeItem(ERR_KEY); setErrors([]); }}
              >
                Clear
              </Button>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

const Row = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className={`text-right break-all ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
  </div>
);

// Snapshot generated from pg_policies. Kept inline so the page works without
// a custom RPC. Refresh by re-running the migration check script.
const POLICY_SNAPSHOT: PolicyRow[] = [
  { tablename: "affiliate_conversions", policyname: "Owner can view", cmd: "SELECT" },
  { tablename: "affiliates", policyname: "Owners can view / limited update", cmd: "SELECT/UPDATE" },
  { tablename: "analytics_events", policyname: "Users insert/read own; admins read all", cmd: "ALL" },
  { tablename: "assessment_answers", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "assessments", policyname: "Users CRUD own; admins view all; no deletes", cmd: "ALL" },
  { tablename: "careers", policyname: "Public read; admins manage", cmd: "ALL" },
  { tablename: "college_recommendations", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "complimentary_invites", policyname: "Admins manage; users see invites to their email", cmd: "ALL" },
  { tablename: "complimentary_invite_audit", policyname: "Admins read", cmd: "SELECT" },
  { tablename: "coupons", policyname: "Admins manage (all CRUD)", cmd: "ALL" },
  { tablename: "coupon_audit_log", policyname: "Admins read; client writes blocked", cmd: "SELECT" },
  { tablename: "daily_challenges", policyname: "Own challenges", cmd: "ALL" },
  { tablename: "email_send_log", policyname: "Service role only", cmd: "ALL" },
  { tablename: "email_send_state", policyname: "Service role only", cmd: "ALL" },
  { tablename: "email_unsubscribe_tokens", policyname: "Service role only", cmd: "ALL" },
  { tablename: "generated_reports", policyname: "Users manage own", cmd: "ALL" },
  { tablename: "global_waitlist", policyname: "Auth users view own; anon blocked", cmd: "ALL" },
  { tablename: "goal_nudges", policyname: "Own nudges", cmd: "ALL" },
  { tablename: "goals", policyname: "Own goals", cmd: "ALL" },
  { tablename: "growth_checkins", policyname: "Users manage own", cmd: "ALL" },
  { tablename: "growth_plans", policyname: "Users manage own", cmd: "ALL" },
  { tablename: "journey_milestones", policyname: "Users manage own; public read opted-in", cmd: "ALL" },
  { tablename: "linkedin_connections", policyname: "Users view/disconnect own", cmd: "SELECT/DELETE" },
  { tablename: "linkedin_audit_log", policyname: "Users view own; client writes blocked", cmd: "SELECT" },
  { tablename: "memberships", policyname: "Users view own; client writes blocked", cmd: "SELECT" },
  { tablename: "mentors", policyname: "Public read active; admins manage", cmd: "ALL" },
  { tablename: "mentor_matches", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "milestones", policyname: "Own milestones", cmd: "ALL" },
  { tablename: "mock_interviews", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "monthly_reflections", policyname: "Users manage own", cmd: "ALL" },
  { tablename: "opportunities", policyname: "Own opportunities", cmd: "ALL" },
  { tablename: "parent_reports", policyname: "Owners manage own", cmd: "ALL" },
  { tablename: "payments", policyname: "Users view own; admins view all; client writes blocked", cmd: "SELECT" },
  { tablename: "payment_refunds", policyname: "Users view own; admins view all", cmd: "SELECT" },
  { tablename: "payment_admin_actions", policyname: "Admins read", cmd: "SELECT" },
  { tablename: "pro_waitlist", policyname: "Anyone can insert; admins view", cmd: "INSERT/SELECT" },
  { tablename: "profiles", policyname: "Users view/insert/update own", cmd: "ALL" },
  { tablename: "razorpay_webhook_events", policyname: "Admins read", cmd: "SELECT" },
  { tablename: "referrals", policyname: "Users view own", cmd: "SELECT" },
  { tablename: "referral_clicks", policyname: "Owners view own; client writes blocked", cmd: "SELECT" },
  { tablename: "report_shares", policyname: "Owners manage own", cmd: "ALL" },
  { tablename: "resumes", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "scholarship_recommendations", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "skill_roadmaps", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "suppressed_emails", policyname: "Service role only", cmd: "ALL" },
  { tablename: "user_rewards", policyname: "Users view own; client writes blocked", cmd: "SELECT" },
  { tablename: "user_roles", policyname: "Users view own; only admins write", cmd: "ALL" },
  { tablename: "user_xp", policyname: "Users view own; public read opted-in", cmd: "SELECT" },
  { tablename: "vision_boards", policyname: "Users manage own", cmd: "ALL" },
  { tablename: "voice_journals", policyname: "Users CRUD own", cmd: "ALL" },
  { tablename: "xp_events", policyname: "Users view own; client writes blocked", cmd: "SELECT" },
];

export default AdminBackend;
